import {
  TicketIcon,
  CalendarIcon,
  ClockCounterClockwiseIcon,
  PlugsIcon,
} from "@phosphor-icons/react";

import Layout from "../components/Layout";
import MrrTile from "../components/billing/MrrTile";
import RmaKpiTile from "../components/rma/RmaKpiTile";
import MonitoringKpiTile from "../components/monitoring/MonitoringKpiTile";
import { useAuth } from "../contexts/AuthContext";

const OFFICE_ROLES = ["owner", "ops"];

const KPI_PLACEHOLDERS = [
  { key: "open_service_requests", label: "Open Service Requests", icon: TicketIcon, unit: "tickets" },
  { key: "scheduled_visits", label: "Scheduled Visits This Week", icon: CalendarIcon, unit: "visits" },
  { key: "overdue_followups", label: "Overdue Follow-ups", icon: ClockCounterClockwiseIcon, unit: "items" },
];

const PHASE_STATUS = [
  ["Auth + RBAC foundation", true],
  ["Shell layout + role-aware nav", true],
  ["Customer CRM + Properties + Systems + Equipment + Warranties", true],
  ["Service Requests + intake + triage", true],
  ["Dispatch board + Work Orders + assignment", true],
  ["Technician status flow + checklist + notes", true],
  ["Diagnostic Reports + photo uploads", true],
  ["PDF export + owner-facing share", true],
  ["Protection Plan subscriptions", true],
  ["Monitoring + Alert-to-ticket", true],
  ["RMA & Warranty claims", true],
  ["Billing + invoicing", true],
  ["SMS alerts (Twilio)", true],
  ["Dispatch-gated payments", true],
  ["Reports & KPI dashboard", true],
];

export default function Dashboard() {
  const { user } = useAuth();
  const isOffice = OFFICE_ROLES.includes(user?.role);

  return (
    <Layout
      title="Operations Dashboard"
      subtitle={user?.name ? `Signed in as ${user.name}` : null}
    >
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          Overview
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 mt-1">
          Today at Zion Solar
        </h1>
        <p className="text-sm text-slate-600 mt-1.5 max-w-2xl leading-relaxed">
          Phase 3 ops live: customers, properties, equipment, warranties,
          service requests, dispatch, and work orders. KPI tiles light up as
          analytics modules ship.
        </p>
      </div>

      <div className="mb-8">
        <MrrTile />
      </div>

      <div
        data-testid="kpi-grid"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        {KPI_PLACEHOLDERS.map((k) => {
          const Icon = k.icon;
          return (
            <div
              key={k.key}
              data-testid={`kpi-${k.key}`}
              className="bg-white border border-slate-200 rounded-md p-5 shadow-card hover:shadow-lift hover:border-slate-300 transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  {k.label}
                </div>
                <Icon size={16} weight="regular" className="text-slate-400" />
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <div className="font-heading text-4xl font-bold text-slate-900 tabular leading-none">
                  —
                </div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                  {k.unit}
                </div>
              </div>
              <div className="mt-2 text-[11px] text-slate-400">
                Awaiting module · phase 2+
              </div>
            </div>
          );
        })}
        {isOffice && <RmaKpiTile />}
        {isOffice && <MonitoringKpiTile />}
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md shadow-card">
          <div className="px-5 py-3.5 border-b border-slate-200">
            <div className="font-heading text-sm font-semibold text-slate-900">
              Recent Activity
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Service requests · work orders · alerts
            </div>
          </div>
          <div
            data-testid="activity-empty"
            className="m-4 border border-dashed border-slate-200 rounded-md p-10 text-center bg-slate-50/40"
          >
            <PlugsIcon
              size={28}
              weight="duotone"
              className="mx-auto text-slate-400"
            />
            <div className="font-heading text-sm font-semibold text-slate-900 mt-3">
              No activity yet
            </div>
            <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto leading-relaxed">
              Activity will appear here as service requests, work orders, and
              monitoring alerts are created in later phases.
            </p>
          </div>
        </div>

        <div
          data-testid="phase-status"
          className="bg-white border border-slate-200 rounded-md shadow-card"
        >
          <div className="px-5 py-3.5 border-b border-slate-200">
            <div className="font-heading text-sm font-semibold text-slate-900">
              Build Status
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Ops &middot; Phase 3
            </div>
          </div>
          <ul className="divide-y divide-slate-100 text-sm">
            {PHASE_STATUS.map(([label, done]) => (
              <li
                key={label}
                className="px-5 py-2.5 flex items-center justify-between gap-3"
              >
                <span className="text-slate-700 truncate">{label}</span>
                <span
                  className={`shrink-0 text-[10px] uppercase tracking-wider font-bold ${
                    done ? "text-emerald-700" : "text-slate-400"
                  }`}
                >
                  {done ? "Live" : "Soon"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Layout>
  );
}
